import { NavLink, useLocation } from "react-router-dom";
import { Dropdown } from "@humansignal/ui";
import { Menu } from "../../components/Menu/Menu";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./ProjectNav.prefix.css";

/**
 * Top-nav shown in the menubar on every project-scoped page. Tabs:
 * Dashboard / Data Manager / Members / Analytics / Settings. Role-
 * gated so annotators only see the tabs they can actually use.
 *
 * Renders in two forms, CSS-swapped by viewport width:
 *   • Expanded pill row on wide screens (>1280px).
 *   • Compact "[Current Tab] ▾" dropdown below that, so the pills
 *     don't collide with the breadcrumb on medium widths.
 *
 * Mounted via each project page's ``.context`` slot.
 */
export const ProjectNav = ({ projectId, canManage, project, userRole }) => {
  const { t } = useTranslation();
  const location = useLocation();
  if (!projectId) return null;

  // DM visibility for non-managers mirrors the project's per-role
  // settings; managers always see it.
  const annotatorsSeeDM = project?.annotators_see_data_manager ?? true;
  const reviewersSeeDM = project?.reviewers_see_data_manager ?? true;
  const showDataManager = canManage || (userRole === "RE" && reviewersSeeDM) || (userRole !== "RE" && annotatorsSeeDM);

  // Text-only pills. The compiled selector for active state is
  // ``.lsf-menu-header__context-pill.lsf-active`` because every class
  // in a ``.prefix.css`` gets the ``lsf-`` prefix at build time —
  // passing a plain "active" to NavLink's activeClassName leaves the
  // DOM with an unstyled class, so we pass the prefixed name.
  const pillClass = cn("menu-header").elem("context-pill").toClassName();
  const activeClass = "lsf-active";

  // Listen (crowd clip validation, Phase A): visible to everyone when
  // the project runs crowd/hybrid validation; managers also see it on
  // any audio-QC project so they can validate in reviewers mode.
  const vmode = project?.audio_qc_config?.validation?.mode;
  const showListen = vmode === "crowd" || vmode === "hybrid" || (canManage && project?.audio_qc_enabled);

  // Bulk mode tab: only when the project has it enabled AND the config
  // actually supports it (single-Choices; the serializer computes this).
  const showBulk = !!project?.bulk_mode_enabled && !!project?.config_suitable_for_bulk_annotation;

  const tabs = [
    { key: "dashboard", to: `/projects/${projectId}`, exact: true, label: t("nav.dashboard") },
    showDataManager && { key: "data", to: `/projects/${projectId}/data`, exact: false, label: t("nav.data_manager") },
    showBulk && {
      key: "bulk",
      to: `/projects/${projectId}/bulk`,
      exact: false,
      label: t("nav.bulk_label", undefined, "Bulk"),
    },
    showListen && { key: "validate", to: `/projects/${projectId}/validate`, exact: false, label: t("nav.listen") },
    canManage && { key: "members", to: `/projects/${projectId}/members`, exact: false, label: t("nav.members") },
    canManage && { key: "analytics", to: `/projects/${projectId}/analytics`, exact: false, label: t("nav.analytics") },
    canManage && { key: "settings", to: `/projects/${projectId}/settings`, exact: false, label: t("nav.settings") },
  ].filter(Boolean);

  const isActive = (tab) => {
    if (tab.exact) return location.pathname === tab.to || location.pathname === `${tab.to}/`;
    return location.pathname.startsWith(tab.to);
  };
  const currentTab = tabs.find(isActive);

  const block = cn("project-nav");

  return (
    <div className={block.toClassName()}>
      {/* Expanded pills — visible on wide screens only. */}
      <nav
        className={block.elem("pills").toClassName()}
        aria-label={t("pages.projectdashboard.project_nav.project_sections")}
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.key}
            to={tab.to}
            exact={tab.exact}
            isActive={(_m, loc) => {
              if (tab.exact) {
                return loc.pathname === tab.to || loc.pathname === `${tab.to}/`;
              }
              return loc.pathname.startsWith(tab.to);
            }}
            className={pillClass}
            activeClassName={activeClass}
            aria-label={tab.label}
            title={tab.label}
            data-external
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* Compact dropdown — visible on narrow screens only. Uses
          ``exact`` on Menu.Item so prefix-based "active" detection
          doesn't mark every entry as the current page (which would
          make items un-clickable via pointer-events: none). */}
      <Dropdown.Trigger
        content={
          <Menu>
            {tabs.map((tab) => (
              <Menu.Item key={`compact-${tab.key}`} label={tab.label} to={tab.to} exact active={isActive(tab)} />
            ))}
          </Menu>
        }
      >
        <button
          type="button"
          className={block.elem("compact-trigger").toClassName()}
          aria-label={t("pages.projectdashboard.project_nav.switch_project_section")}
          title={currentTab?.label ?? t("pages.projectdashboard.project_nav.project_sections")}
        >
          <span className={block.elem("compact-label").toClassName()}>
            {currentTab?.label ?? t("pages.projectdashboard.project_nav.project_sections")}
          </span>
          {/* Chevron matches the app's select controls (16px, 2px
              stroke) — the old 10px 1.5-stroke one read as a stray
              tick mark next to the label. */}
          <svg
            className={block.elem("compact-caret").toClassName()}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </Dropdown.Trigger>
    </div>
  );
};
